import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {Chessboard} from "react-chessboard";
import {Chess} from "chess.js";
import {findCheckedKing} from "../utils/chessFunctions";
import {playSound} from "../utils/sounds";

const LIGHT = "#f3ead3";
const DARK = "#93b47c";

const HIGHLIGHT_LAST = "rgba(240, 194, 94, 0.5)";
const HIGHLIGHT_SELECTED = "rgba(92, 139, 74, 0.55)";
const HIGHLIGHT_PREMOVE = "rgba(95, 157, 178, 0.55)";
const DOT = "radial-gradient(circle, rgba(46, 58, 44, 0.26) 21%, transparent 23%)";
const RING = "radial-gradient(circle, transparent 57%, rgba(46, 58, 44, 0.26) 59%, rgba(46, 58, 44, 0.26) 71%, transparent 73%)";
const CHECK = "radial-gradient(circle, rgba(214, 72, 52, 0.95) 0%, rgba(214, 72, 52, 0.55) 38%, rgba(214, 72, 52, 0) 72%)";

// Measures the available width so the board always fills its column (and never overflows the screen)
const useBoardWidth = (ref, maxWidth) => {
    const [width, setWidth] = useState(0);
    useLayoutEffect(() => {
        if (!ref.current) return;
        const element = ref.current;
        const measure = () => {
            const frame = element.firstElementChild;
            const padding = frame ? parseFloat(getComputedStyle(frame).paddingLeft || "0") * 2 : 0;
            const available = element.clientWidth - padding;
            const next = Math.floor(Math.min(available, maxWidth || Infinity));
            setWidth((prev) => (Math.abs(prev - next) > 1 ? next : prev));
        };
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(element);
        return () => observer.disconnect();
    }, [ref, maxWidth]);
    return width;
};

const squareToCoords = (square, orientation) => {
    const file = square.charCodeAt(0) - 97;
    const rank = parseInt(square[1], 10) - 1;
    if (orientation === "white") return {col: file, row: 7 - rank};
    return {col: 7 - file, row: rank};
};

/**
 * Chess board used everywhere on the site.
 *
 * props:
 *  - fen: position to show
 *  - orientation: "white" | "black"
 *  - onMove(move, newFen): called with the chess.js move object when a legal move is made
 *  - canMove: whether the user may move pieces at all
 *  - playerColor: "w" | "b" to only allow moving one colour, or null/undefined for both
 *  - lastMove: {from, to} to highlight
 *  - arrows: [[from, to, color]]
 *  - badge: {square, symbol: (size) => ReactNode} - small marker drawn on a square (used by analysis)
 *  - maxWidth: optional maximum width in px
 *  - premoves: let the player pick up their pieces and queue one move while the opponent is thinking (needs playerColor)
 *  - annotations: {arrows: [{from, to, color, label, opacity}], rings: [{square}]} drawn on top of the board, used to
 *    show Rook's lines with numbered arrows and the squares Rook talks about
 */
const ChessBoard = ({
                        fen,
                        orientation = "white",
                        onMove,
                        canMove = true,
                        playerColor,
                        lastMove,
                        arrows,
                        badge,
                        annotations,
                        maxWidth,
                        id = "chess-board",
                        locked = false,
                        previewing = false,
                        premoves = false
                    }) => {
    const containerRef = useRef(null);
    const boardWidth = useBoardWidth(containerRef, maxWidth);

    const [selected, setSelected] = useState(null);
    const [targets, setTargets] = useState([]);
    const [pendingPromotion, setPendingPromotion] = useState(null);
    // A move queued during the opponent's turn: {from, to, promotion}. It is played as soon as it is our turn (if it is
    // still legal then), otherwise it is dropped.
    const [premove, setPremove] = useState(null);

    // Clear any selection when the position changes underneath us (opponent moved, stepping through history...)
    useEffect(() => {
        setSelected(null);
        setTargets([]);
        setPendingPromotion(null);
    }, [fen, canMove]);

    // Play the queued premove as soon as it is our turn. It is dropped if it is no longer legal, or if the board is
    // no longer ours to move on (game over, looking back through the moves...).
    useEffect(() => {
        if (!premove) return;
        if (!canMove || locked) {
            setPremove(null);
            return;
        }
        let turn;
        try {
            turn = new Chess(fen).turn();
        } catch (e) {
            return;
        }
        if (turn !== playerColor) return;
        const queued = premove;
        setPremove(null);
        tryMoveRef.current(queued.from, queued.to, queued.promotion);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fen, canMove, locked]);

    const chess = useMemo(() => {
        try {
            return new Chess(fen);
        } catch (e) {
            console.error("Invalid fen given to board", fen, e);
            return new Chess();
        }
    }, [fen]);

    const premoveMode = premoves && !!playerColor && canMove && !locked && chess.turn() !== playerColor;

    const mayMoveColor = useCallback((color) => {
        if (!canMove || locked) return false;
        if (premoveMode) return color === playerColor;
        if (color !== chess.turn()) return false;
        return !playerColor || playerColor === color;
    }, [canMove, locked, chess, playerColor, premoveMode]);

    // Where a piece could go once it is our turn: its moves as if it were our turn already, plus the diagonal squares
    // for pawns (the opponent may put something there to capture)
    const premoveTargets = useCallback((square) => {
        const parts = fen.split(" ");
        parts[1] = playerColor;
        parts[3] = "-";
        let targets = [];
        try {
            targets = new Chess(parts.join(" ")).moves({square, verbose: true}).map((m) => ({to: m.to, capture: !!m.captured}));
        } catch (e) {
            targets = [];
        }
        const piece = chess.get(square);
        if (piece && piece.type === "p") {
            const file = square.charCodeAt(0);
            const rank = parseInt(square[1], 10) + (piece.color === "w" ? 1 : -1);
            [file - 1, file + 1].forEach((f) => {
                if (f < 97 || f > 104 || rank < 1 || rank > 8) return;
                const to = String.fromCharCode(f) + rank;
                if (!targets.some((t) => t.to === to)) targets.push({to, capture: true});
            });
        }
        return targets;
    }, [fen, chess, playerColor]);

    const movesFrom = useCallback((square) => (premoveMode
        ? premoveTargets(square)
        : chess.moves({square, verbose: true}).map((m) => ({to: m.to, capture: !!m.captured}))
    ), [premoveMode, premoveTargets, chess]);

    const queuePremove = (from, to) => {
        const piece = chess.get(from);
        const promotes = piece && piece.type === "p" && (to[1] === "8" || to[1] === "1");
        setPremove({from, to, promotion: promotes ? "q" : undefined});
        setSelected(null);
        setTargets([]);
    };

    const tryMove = useCallback((from, to, promotion) => {
        if (!onMove) return false;
        const working = new Chess(fen);
        let move;
        try {
            move = working.move({from, to, promotion: promotion || undefined});
        } catch (e) {
            move = null;
        }
        if (!move) {
            return false;
        }
        setSelected(null);
        setTargets([]);
        onMove(move, working.fen());
        return true;
    }, [fen, onMove]);

    const tryMoveRef = useRef(tryMove);
    tryMoveRef.current = tryMove;

    const isPromotionMove = useCallback((from, to) => {
        const piece = chess.get(from);
        if (!piece || piece.type !== "p") return false;
        if (!(to[1] === "8" || to[1] === "1")) return false;
        return chess.moves({square: from, verbose: true}).some((m) => m.to === to && m.promotion);
    }, [chess]);

    const selectSquare = (square) => {
        const piece = chess.get(square);
        if (piece && mayMoveColor(piece.color)) {
            setSelected(square);
            setTargets(movesFrom(square));
            return true;
        }
        setSelected(null);
        setTargets([]);
        return false;
    };

    const onSquareClick = (square) => {
        if (!canMove || locked) return;
        // Clicking anywhere cancels a queued premove (and may start a new one)
        if (premove && !selected) setPremove(null);
        if (selected) {
            if (selected === square) {
                setSelected(null);
                setTargets([]);
                return;
            }
            if (targets.some((t) => t.to === square)) {
                if (premoveMode) {
                    queuePremove(selected, square);
                    return;
                }
                if (isPromotionMove(selected, square)) {
                    setPendingPromotion({from: selected, to: square});
                    return;
                }
                tryMove(selected, square);
                return;
            }
        }
        selectSquare(square);
    };

    const onPieceDrop = (from, to) => {
        if (premoveMode) {
            // The piece goes back to its square; the queued move is shown with highlighted squares instead
            if (from !== to && premoveTargets(from).some((t) => t.to === to)) queuePremove(from, to);
            else {
                setSelected(null);
                setTargets([]);
            }
            return false;
        }
        const ok = tryMove(from, to);
        if (!ok && from !== to) playSound("illegal");
        return ok;
    };

    const onPieceDragBegin = (piece, square) => {
        if (mayMoveColor(piece[0])) {
            if (premove) setPremove(null);
            setSelected(square);
            setTargets(movesFrom(square));
        }
    };

    const onPromotionCheck = (from, to) => !premoveMode && isPromotionMove(from, to);

    const onPromotionPieceSelect = (piece, from, to) => {
        const source = from || pendingPromotion?.from;
        const target = to || pendingPromotion?.to;
        setPendingPromotion(null);
        if (!piece || !source || !target) {
            setSelected(null);
            setTargets([]);
            return false;
        }
        return tryMove(source, target, piece[1].toLowerCase());
    };

    const isDraggablePiece = ({piece}) => mayMoveColor(piece[0]);

    const squareStyles = useMemo(() => {
        // Each square gets a stack of background layers drawn on top of its own colour
        const layers = {};
        const add = (square, layer) => {
            layers[square] = [layer, ...(layers[square] || [])];
        };
        const tint = (color) => `linear-gradient(${color}, ${color})`;

        if (lastMove?.from) {
            add(lastMove.from, tint(HIGHLIGHT_LAST));
            add(lastMove.to, tint(HIGHLIGHT_LAST));
        }
        if (premove) {
            add(premove.from, tint(HIGHLIGHT_PREMOVE));
            add(premove.to, tint(HIGHLIGHT_PREMOVE));
        }
        if (selected) add(selected, tint(premoveMode ? HIGHLIGHT_PREMOVE : HIGHLIGHT_SELECTED));
        const checked = findCheckedKing(fen);
        if (checked) add(checked, CHECK);
        targets.forEach(({to, capture}) => add(to, capture ? RING : DOT));

        const styles = {};
        Object.entries(layers).forEach(([square, squareLayers]) => {
            const isDark = (square.charCodeAt(0) - 97 + parseInt(square[1], 10) - 1) % 2 === 0;
            styles[square] = {background: `${squareLayers.join(", ")}, ${isDark ? DARK : LIGHT}`};
        });
        targets.forEach(({to}) => {
            styles[to] = {...styles[to], cursor: "pointer"};
        });
        return styles;
    }, [fen, lastMove, selected, targets, premove, premoveMode]);

    const badgeNode = useMemo(() => {
        if (!badge?.square || !boardWidth) return null;
        const {col, row} = squareToCoords(badge.square, orientation);
        const size = boardWidth / 8;
        return (
            <div className="square-badge" key={`${badge.square}-${fen}`} style={{
                left: col * size + size * 0.6,
                top: row * size - size * 0.08,
                width: size * 0.48,
                height: size * 0.48,
                right: "auto"
            }}>
                {badge.symbol(size * 0.48)}
            </div>
        );
    }, [badge, boardWidth, orientation, fen]);

    const annotationNode = useMemo(() => {
        if (!boardWidth || !annotations || (!annotations.arrows?.length && !annotations.rings?.length)) return null;
        const center = (square) => {
            const {col, row} = squareToCoords(square, orientation);
            return {x: col + 0.5, y: row + 0.5};
        };
        return (
            <svg className="board-annotations" viewBox="0 0 8 8" width={boardWidth} height={boardWidth} aria-hidden="true">
                {(annotations.rings || []).map(({square}) => {
                    const c = center(square);
                    return <circle key={`ring-${square}`} className="board-annotations__ring" cx={c.x} cy={c.y} r="0.43"/>;
                })}
                {(annotations.arrows || []).map((arrow, i) => {
                    const a = center(arrow.from);
                    const b = center(arrow.to);
                    const dx = b.x - a.x;
                    const dy = b.y - a.y;
                    const length = Math.hypot(dx, dy) || 1;
                    const ux = dx / length;
                    const uy = dy / length;
                    // Stop the shaft before the head, and put the head's tip a little inside the target square
                    const tip = {x: b.x - ux * 0.18, y: b.y - uy * 0.18};
                    const base = {x: tip.x - ux * 0.36, y: tip.y - uy * 0.36};
                    const side = {x: -uy * 0.2, y: ux * 0.2};
                    const mid = {x: a.x + dx * 0.5, y: a.y + dy * 0.5};
                    const badge = length < 1.2 ? {x: a.x + ux * 0.22, y: a.y + uy * 0.22} : mid;
                    return (
                        <g key={`${arrow.from}${arrow.to}${i}`} opacity={arrow.opacity ?? 1}>
                            <line x1={a.x + ux * 0.12} y1={a.y + uy * 0.12} x2={base.x} y2={base.y}
                                  stroke={arrow.color} strokeWidth="0.15" strokeLinecap="round"/>
                            <polygon points={`${tip.x},${tip.y} ${base.x + side.x},${base.y + side.y} ${base.x - side.x},${base.y - side.y}`}
                                     fill={arrow.color}/>
                            {arrow.label && (
                                <g>
                                    <circle cx={badge.x} cy={badge.y} r="0.2" fill={arrow.color} stroke="#fffdf7" strokeWidth="0.035"/>
                                    <text x={badge.x} y={badge.y + 0.085} textAnchor="middle" fontSize="0.24" fontWeight="800"
                                          fill="#fffdf7" fontFamily="Nunito, sans-serif">{arrow.label}</text>
                                </g>
                            )}
                        </g>
                    );
                })}
            </svg>
        );
    }, [annotations, boardWidth, orientation]);

    return (
        <div className="board-wrap" ref={containerRef}>
            <div className={`board-frame ${locked ? "board-frame--locked" : ""} ${previewing ? "board-frame--preview" : ""}`}>
                <div className="board-frame__inner" style={{position: "relative", width: boardWidth || undefined}}>
                    {boardWidth > 0 && (
                        <Chessboard
                            id={id}
                            position={fen}
                            boardWidth={boardWidth}
                            boardOrientation={orientation}
                            animationDuration={180}
                            arePiecesDraggable={canMove && !locked}
                            isDraggablePiece={isDraggablePiece}
                            onPieceDrop={onPieceDrop}
                            onPieceDragBegin={onPieceDragBegin}
                            onSquareClick={onSquareClick}
                            onPromotionCheck={onPromotionCheck}
                            onPromotionPieceSelect={onPromotionPieceSelect}
                            showPromotionDialog={!!pendingPromotion}
                            promotionToSquare={pendingPromotion?.to || null}
                            promotionDialogVariant="modal"
                            onSquareRightClick={() => setPremove(null)}
                            customSquareStyles={squareStyles}
                            customLightSquareStyle={{backgroundColor: LIGHT}}
                            customDarkSquareStyle={{backgroundColor: DARK}}
                            customDropSquareStyle={{boxShadow: "inset 0 0 0 4px rgba(92, 139, 74, 0.75)"}}
                            customNotationStyle={{fontWeight: 800, fontSize: `${Math.max(10, boardWidth / 44)}px`, opacity: 0.85}}
                            customArrows={arrows || []}
                            customArrowColor="rgba(226, 132, 154, 0.85)"
                            areArrowsAllowed={true}
                            snapToCursor={true}
                        />
                    )}
                    {badgeNode}
                    {annotationNode}
                </div>
            </div>
        </div>
    );
};

export default ChessBoard;
